import { useAtom, useAtomValue } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useEffect, useMemo } from 'react';
import { boardStateAtom, draftSelectionIdsAtom, isPanningAtom, isSelectingAtom, presentationModeAtom, rulerStateAtom, selectedIdsAtom, selectionRectAtom, stageModeAtom, stageRefAtom, } from '@/store/boardAtoms';
import { MouseButton } from '@/types/boardTypes';
import { getAllGroupElementIds } from '@/utils/board/boardUtils';
import { calculateRulerDistance, findElementWithLocalGrid } from '@/utils/board/rulerUtils';
import { getElementsMap, isElementShape, toWorld } from '@/utils/board/stageUtils';
import { isEqualArrays } from '@/utils/sort';
/**
 * Кастомный хук для обработки событий мыши на Stage
 *
 * @param params - Параметры хука
 * @returns Объект с обработчиками событий мыши
 */
export const useStageMouseEvents = ({ boardState, canEdit, visibleElements, pointerPositionRef, wasStageDraggedRef, elementClickId, isPresentationMode = false, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Атомы состояния
    const [stageMode, setStageMode] = useAtom(stageModeAtom);
    const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);
    const [draftSelectionIds, setDraftSelectionIds] = useAtom(draftSelectionIdsAtom);
    const [isSelecting, setIsSelecting] = useAtom(isSelectingAtom);
    const [selectionRect, setSelectionRect] = useAtom(selectionRectAtom);
    const [isPanning, setIsPanning] = useAtom(isPanningAtom);
    const [rulerState, setRulerState] = useAtom(rulerStateAtom);
    /**
     * Мемоизированная функция для определения пересечений элементов с выделением
     */
    const getIntersectingElements = useMemo(() => (rect) => {
        var _a, _b, _c, _d;
        const rx = Math.min(rect.x, rect.x + rect.w);
        const ry = Math.min(rect.y, rect.y + rect.h);
        const rw = Math.abs(rect.w);
        const rh = Math.abs(rect.h);
        const ids = [];
        for (const el of visibleElements) {
            // Пропускаем заблокированные элементы и презентационные фреймы (как locked элементы)
            if (el.locked || el.isPresentationFrame)
                continue;
            const ex = ((_a = el.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
            const ey = ((_b = el.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
            const ew = ((_c = el.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
            const eh = ((_d = el.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
            if (ex < rx + rw && ex + ew > rx && ey < ry + rh && ey + eh > ry) {
                ids.push(el.id);
            }
        }
        return ids;
    }, [visibleElements]);
    /**
     * Получает информацию о клике по элементу
     */
    const getElementClickInfo = useCallback((event) => {
        var _a, _b, _c;
        let target = event.target;
        let isClickOnElement = false;
        let element = null;
        let isLockedElement = false;
        let layerLocked = false;
        if (wasStageDraggedRef.current) {
            // Двигается Stage, игнорируем клик по элементу
            return { isClickOnElement, element, isLockedElement, layerLocked, target };
        }
        if (((_a = event.currentTarget) === null || _a === void 0 ? void 0 : _a.mouseClickStartShape) &&
            isElementShape(event.currentTarget.mouseClickStartShape)) {
            target = event.currentTarget.mouseClickStartShape;
        }
        const elementsMap = getElementsMap(boardState.layers);
        while (target && isElementShape(target)) {
            const id = (_b = target.attrs) === null || _b === void 0 ? void 0 : _b.id;
            if (id && /^node-\d+$/.test(id)) {
                const elementId = Number(id.replace('node-', ''));
                const elementData = elementsMap.get(elementId);
                if (elementData) {
                    element = elementData.el;
                    isLockedElement = element.locked || false;
                    layerLocked = ((_c = elementData.layer) === null || _c === void 0 ? void 0 : _c.locked) || false;
                    isClickOnElement = true;
                }
                break;
            }
            if (!target.parent)
                break;
            target = target.parent;
        }
        return { isClickOnElement, element, isLockedElement, layerLocked, target };
    }, [boardState.layers, wasStageDraggedRef]);
    /**
     * Обработчик нажатия мыши на Stage
     */
    const onStageMouseDown = useCallback((event) => {
        if (draftSelectionIds.length > 0) {
            setDraftSelectionIds([]); // Сбрасываем пересечения при нажатии
        }
        // Режим "рука"
        const isMiddleOrRightBtn = event.evt.button === MouseButton.MIDDLE || event.evt.button === MouseButton.RIGHT;
        if (isMiddleOrRightBtn || stageMode === 'move') {
            setIsPanning(true);
            wasStageDraggedRef.current = false;
            const stage = store.get(stageRefAtom);
            if (stage && typeof stage.startDrag === 'function') {
                stage.startDrag();
            }
            return;
        }
        // Режим "линейка"
        if (stageMode === 'ruler' && event.evt.button === MouseButton.LEFT) {
            const stage = store.get(stageRefAtom);
            if (!stage)
                return;
            const pointer = stage.getPointerPosition();
            if (!pointer)
                return;
            const worldPointer = toWorld(stage, pointer);
            const elementWithGrid = findElementWithLocalGrid(stage, worldPointer.x, worldPointer.y, boardState);
            setRulerState({
                isActive: true,
                startPoint: worldPointer,
                endPoint: worldPointer,
                distance: 0,
                unit: elementWithGrid ? 'cells' : 'px',
            });
            return;
        }
        // Режим "выделения"
        // В режиме презентации выделение запрещено
        if (isPresentationMode) {
            return;
        }
        const { isClickOnElement, element, isLockedElement } = getElementClickInfo(event);
        if (isClickOnElement && element) {
            elementClickId.current = element.id;
        }
        const multiSelection = event.evt.shiftKey || event.evt.ctrlKey;
        // Если клик по Stage или по заблокированному элементу — начинаем выделение рамкой
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        if (!multiSelection && (event.target === stage || isLockedElement)) {
            const pointer = toWorld(stage, stage.getPointerPosition());
            setSelectionRect({ x: pointer.x, y: pointer.y, w: 0, h: 0 });
            setIsSelecting(true);
            if (selectedIds.length > 0) {
                setSelectedIds([]);
            }
        }
    }, [
        draftSelectionIds.length,
        stageMode,
        isPresentationMode,
        getElementClickInfo,
        store,
        setDraftSelectionIds,
        setIsPanning,
        wasStageDraggedRef,
        boardState,
        setRulerState,
        elementClickId,
        setSelectionRect,
        setIsSelecting,
        selectedIds.length,
        setSelectedIds,
    ]);
    /**
     * Обработчик движения мыши на Stage
     */
    const onStageMouseMove = useCallback((event) => {
        // Определение позиции курсора
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        // pointer (getPointerPosition) - всегда левый верхний угол текущего вида == 0,0
        const pointer = stage.getPointerPosition();
        const worldPointer = toWorld(stage, pointer);
        if (pointer) {
            // Проверяем, находится ли курсор внутри канваса
            if (pointer &&
                pointer.x >= 0 &&
                pointer.x <= stage.width() &&
                pointer.y >= 0 &&
                pointer.y <= stage.height()) {
                // Переводим координаты в "мир" доски
                const scale = stage.scaleX();
                const x = (pointer.x - stage.x()) / scale;
                const y = (pointer.y - stage.y()) / scale;
                pointerPositionRef.current = { x, y };
            }
            else {
                pointerPositionRef.current = null;
            }
        }
        else {
            pointerPositionRef.current = null;
        }
        // Режим "линейка"
        if (stageMode === 'ruler' && rulerState.isActive && rulerState.startPoint) {
            const elementWithGrid = findElementWithLocalGrid(stage, rulerState.startPoint.x, rulerState.startPoint.y, boardState);
            const { distance, unit } = calculateRulerDistance(rulerState.startPoint, worldPointer, elementWithGrid);
            setRulerState((prev) => (Object.assign(Object.assign({}, prev), { endPoint: worldPointer, distance,
                unit })));
            return;
        }
        if (stageMode === 'select') {
            // В режиме презентации выделение запрещено
            if (isPresentationMode) {
                return;
            }
            // Режим "Выделение"
            if (!isSelecting || !selectionRect)
                return;
            setSelectionRect((rect) => rect ? Object.assign(Object.assign({}, rect), { w: worldPointer.x - rect.x, h: worldPointer.y - rect.y }) : null);
        }
        // Определение пересечений выделения с объектами
        // В режиме презентации выделение запрещено
        if (isPresentationMode) {
            return;
        }
        if (isSelecting && selectionRect) {
            const rect = {
                x: selectionRect.x,
                y: selectionRect.y,
                w: worldPointer.x - selectionRect.x,
                h: worldPointer.y - selectionRect.y,
            };
            setSelectionRect(rect);
            // Детект пересечений
            const ids = getIntersectingElements(rect);
            // Расширяем выделение: если элемент в группе, добавляем всю группу
            const expandedIds = getAllGroupElementIds(boardState, ids);
            const expandedIdsArray = Array.from(expandedIds);
            if (!isEqualArrays(expandedIdsArray, draftSelectionIds)) {
                setDraftSelectionIds(expandedIdsArray);
            }
        }
    }, [
        store,
        stageMode,
        rulerState.isActive,
        rulerState.startPoint,
        isPresentationMode,
        isSelecting,
        selectionRect,
        pointerPositionRef,
        boardState,
        setRulerState,
        setSelectionRect,
        getIntersectingElements,
        draftSelectionIds,
        setDraftSelectionIds,
    ]);
    /**
     * Обработчик отпускания кнопки мыши на Stage
     */
    const onStageMouseUp = useCallback((event) => {
        if (event.evt.button !== MouseButton.LEFT) {
            // Обработка средней и правой кнопки мыши после панорамирования
            // В режиме презентации выделение запрещено
            if (!isPresentationMode && draftSelectionIds.length) {
                // Расширяем выделение: если элемент в группе, добавляем всю группу
                const expandedIds = getAllGroupElementIds(boardState, draftSelectionIds);
                setSelectedIds(Array.from(expandedIds));
                setDraftSelectionIds([]);
            }
        }
        const { isClickOnElement, element, isLockedElement, layerLocked, target } = getElementClickInfo(event);
        if (element && process.env.NODE_ENV === 'development') {
            console.log('onStageMouseUp ~ element: ', element, 'target: ', target);
        }
        if (isPanning) {
            // Если было перемещение — завершаем перемещение
            setIsPanning(false);
            wasStageDraggedRef.current = false;
        }
        // Режим "линейка" - сбрасываем состояние при отпускании мыши и переключаемся на select
        if (stageMode === 'ruler' && event.evt.button === MouseButton.LEFT) {
            setRulerState({
                isActive: false,
                startPoint: null,
                endPoint: null,
                distance: null,
                unit: 'px',
            });
            // Переключаемся на режим select после завершения замеров
            setStageMode('select');
            return;
        }
        if (stageMode === 'select') {
            // В режиме презентации выделение запрещено
            if (isPresentationMode) {
                setIsSelecting(false);
                setSelectionRect(null);
                return;
            }
            // Обработка клика по элементу
            if (isClickOnElement && element) {
                if (stageMode !== 'select' || isPanning)
                    return;
                if (event.evt.button !== MouseButton.LEFT)
                    return;
                elementClickId.current = element.id;
                const multi = event.evt.shiftKey || event.evt.ctrlKey || event.evt.metaKey;
                const isSingleClick = ((selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.w) || 0) === 0 && ((selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.h) || 0) === 0;
                // Обработка мультиселекта (для заблокированных и незаблокированных элементов)
                if (multi) {
                    // Мульти-выбор: добавляем/убираем только кликнутый элемент (оригинальная логика)
                    setSelectedIds((prev) => prev.includes(element.id)
                        ? prev.filter((i) => i !== element.id)
                        : [...prev, element.id]);
                    elementClickId.current = null;
                    return;
                }
                // Обычный клик на заблокированном элементе
                if (isSingleClick && (isLockedElement || layerLocked)) {
                    // Если элемент в группе, выделяем всю группу, иначе только элемент
                    const groupElementIds = getAllGroupElementIds(boardState, [element.id]);
                    setSelectedIds(Array.from(groupElementIds));
                    setSelectionRect(null);
                    setIsSelecting(false);
                    return;
                }
                // Обычный клик на незаблокированном элементе
                if (!isLockedElement && !layerLocked) {
                    if (!selectedIds.includes(element.id) && !draftSelectionIds.includes(element.id)) {
                        // Обычный клик: выделяем всю группу элемента (если есть группа), иначе только элемент
                        // getAllGroupElementIds всегда включает исходный элемент, так что для элементов без группы
                        // вернется Set только с этим элементом
                        const groupElementIds = getAllGroupElementIds(boardState, [element.id]);
                        setSelectedIds(Array.from(groupElementIds));
                    }
                    elementClickId.current = null;
                }
            }
            // Обработка выделения рамкой
            if (!isSelecting || !selectionRect) {
                setIsSelecting(false);
                setSelectionRect(null);
                return;
            }
            if (draftSelectionIds.length > 0) {
                // Расширяем выделение: если элемент в группе, добавляем всю группу
                const expandedIds = getAllGroupElementIds(boardState, draftSelectionIds);
                setSelectedIds(Array.from(expandedIds));
                setDraftSelectionIds([]);
            }
            setSelectionRect(null);
            setIsSelecting(false);
        }
    }, [
        getElementClickInfo,
        isPanning,
        stageMode,
        isPresentationMode,
        draftSelectionIds,
        boardState,
        setSelectedIds,
        setDraftSelectionIds,
        setIsPanning,
        wasStageDraggedRef,
        setRulerState,
        setStageMode,
        isSelecting,
        selectionRect,
        setSelectionRect,
        setIsSelecting,
        elementClickId,
        selectedIds,
    ]);
    /**
     * Обработчик выхода курсора за границы Stage
     */
    const onStageMouseLeave = useCallback((e) => {
        pointerPositionRef.current = null;
    }, [pointerPositionRef]);
    /**
     * Обработчик двойного клика по Stage/Element
     */
    const onStageDoubleClick = useCallback((e) => {
        // console.log('onStageDoubleClick');
        // TODO: Редактирование маски по двойному клику
        // const { isClickOnElement, element, isLockedElement, layerLocked } = getElementClickInfo(e);
        // if (isClickOnElement && element && !isLockedElement && !layerLocked) {
        //   setMaskEditor({
        //     active: true,
        //     element: element,
        //     mode: 'rect',
        //     rect: null,
        //     circle: null,
        //   });
        // }
    }, []);
    /**
     * Обработчик контекстного меню
     */
    const handleContextMenu = useCallback((e) => {
        e.evt.preventDefault();
        if (isPanning)
            return;
        const target = e.target;
        console.log('target: ', target);
        console.log('isElementShape(target): ', isElementShape(target));
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        if (target === stage) {
            return;
        }
        if (isElementShape(target)) {
            return;
        }
    }, [isPanning, store]);
    return {
        onStageMouseDown,
        onStageMouseMove,
        onStageMouseUp,
        onStageMouseLeave,
        onStageDoubleClick,
        handleContextMenu,
        getElementClickInfo,
    };
};
// Глобальная обработка mouseup для завершения выделения при отпускании кнопки вне Stage
export const useGlobalMouseUpForSelection = () => {
    const [isSelecting, setIsSelecting] = useAtom(isSelectingAtom);
    const [draftSelectionIds, setDraftSelectionIds] = useAtom(draftSelectionIdsAtom);
    const [, setSelectedIds] = useAtom(selectedIdsAtom);
    const [, setSelectionRect] = useAtom(selectionRectAtom);
    const [presentationMode] = useAtom(presentationModeAtom);
    const boardState = useAtomValue(boardStateAtom);
    useEffect(() => {
        const onMouseUp = () => {
            // В режиме презентации выделение запрещено
            if (presentationMode) {
                setIsSelecting(false);
                setSelectionRect(null);
                return;
            }
            if (!isSelecting)
                return;
            if (draftSelectionIds.length > 0) {
                // Расширяем выделение: если элемент в группе, добавляем всю группу
                const expandedIds = getAllGroupElementIds(boardState, draftSelectionIds);
                setSelectedIds(Array.from(expandedIds));
                setDraftSelectionIds([]);
            }
            setSelectionRect(null);
            setIsSelecting(false);
        };
        window.addEventListener('mouseup', onMouseUp);
        return () => window.removeEventListener('mouseup', onMouseUp);
    }, [
        isSelecting,
        draftSelectionIds,
        boardState,
        setSelectedIds,
        setDraftSelectionIds,
        setIsSelecting,
        setSelectionRect,
        presentationMode,
    ]);
};
